import { z } from "zod";

import type { HandledErrorFault, HandledErrorStatus } from "./handled-error.ts";

// A failure recorded for someone to read later, not thrown at a caller (ADR-001, revision 1):
// why a run stopped, why a lane job died. Each code is declared once, in TypeScript, with its
// fault and status and the names of its params; the Go worker gets the same catalogue from
// `pnpm generate:contracts`. Params are whole numbers only, so no text rides along with a code.

export type FailureEntry = {
  readonly fault: HandledErrorFault;
  readonly status: HandledErrorStatus;
  /** The whole-number params the code carries, by name; never text. */
  readonly params?: readonly string[];
};

export type FailureCatalogue = Readonly<Record<string, FailureEntry>>;

export type FailureCodeOf<TCatalogue extends FailureCatalogue> = keyof TCatalogue & string;

type ParamNames<TEntry> = TEntry extends { readonly params: readonly (infer TName extends string)[] }
  ? TName
  : never;

/** A code's params as a reader gets them: any may be absent on a record older than the param. */
export type FailureParamsOf<
  TCatalogue extends FailureCatalogue,
  TCode extends FailureCodeOf<TCatalogue>,
> = Readonly<Partial<Record<ParamNames<TCatalogue[TCode]>, number>>>;

const CODE = /^[a-z]+(?:-[a-z]+)*$/;
const PARAM = /^[a-z][a-zA-Z]*$/;

/** Declares a catalogue; refuses a code or a param name the Go generator could not carry. */
export function defineFailures<const TCatalogue extends FailureCatalogue>(catalogue: TCatalogue): TCatalogue {
  for (const [code, entry] of Object.entries(catalogue)) {
    if (!CODE.test(code)) throw new Error(`Failure code "${code}" is not lower-case words joined by "-".`);
    for (const param of entry.params ?? []) {
      if (!PARAM.test(param)) throw new Error(`Failure "${code}" names the param "${param}", not camelCase.`);
    }
  }
  return Object.freeze(catalogue);
}

/** Every code of a catalogue, in its order, as `z.enum` takes them. */
export function failureCodes<TCatalogue extends FailureCatalogue>(
  catalogue: TCatalogue,
): [FailureCodeOf<TCatalogue>, ...FailureCodeOf<TCatalogue>[]] {
  const codes = Object.keys(catalogue) as FailureCodeOf<TCatalogue>[];
  const [first, ...rest] = codes;
  if (first === undefined) throw new Error("A failure catalogue declares at least one code.");
  return [first, ...rest];
}

/** What a store keeps for a failure: its code and whole-number params, nothing else. */
export const storedFailureSchema = z.object({
  code: z.string(),
  params: z.record(z.string(), z.number().int()).default({}),
});
export type StoredFailure = z.infer<typeof storedFailureSchema>;
